import React, { useState } from 'react';
import { CheckCircle2, Calendar, Clock, User, ArrowRight, ShieldCheck, MessageCircle, CreditCard, Sparkles } from 'lucide-react';
import { Service } from './types';
import { getThemeTokens } from '@/lib/themeTokens';

interface SuccessViewProps {
  businessName?: string;
  portalTheme?: string;
  brandColor?: string;
  confirmedBooking: any;
  selectedServices: Service[];
  assignedStaffName: string;
  formattedDateString: string;
  selectedTime: string;
  onGoHome: () => void;
  onOpenMyBookings: () => void;
}

export const SuccessView: React.FC<SuccessViewProps> = ({
  businessName = 'el comercio',
  portalTheme,
  brandColor,
  confirmedBooking,
  selectedServices,
  assignedStaffName,
  formattedDateString,
  selectedTime,
  onGoHome,
  onOpenMyBookings,
}) => {
  const theme = getThemeTokens(portalTheme, brandColor);
  const [payingDeposit, setPayingDeposit] = useState(false);
  const [depositMsg, setDepositMsg] = useState<string | null>(null);

  const totalCashPrice = selectedServices.reduce((acc, s) => acc + s.cashPrice, 0);

  const handlePayDeposit = async () => {
    if (!confirmedBooking?.id) return;
    setPayingDeposit(true);
    setDepositMsg(null);
    try {
      const res = await fetch('/api/payments/create-preference', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointmentId: confirmedBooking.id })
      });
      const data = await res.json();
      if (data.success && data.preference) {
        setDepositMsg(`✅ Solicitud de seña generada (${data.preference.externalReference}). Monto: $${data.preference.amount}.`);
      } else {
        setDepositMsg(data.error || 'No se pudo generar la solicitud de pago.');
      }
    } catch (e: any) {
      setDepositMsg('Error al conectar con Mercado Pago: ' + e.message);
    } finally {
      setPayingDeposit(false);
    }
  };

  const depositAmount = confirmedBooking?.depositAmount || 0;

  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [whatsAppSent, setWhatsAppSent] = useState(false);
  const [whatsAppFeedback, setWhatsAppFeedback] = useState<string | null>(null);

  const handleSendWhatsAppConfirmation = async () => {
    if (!confirmedBooking?.id) return;
    setSendingWhatsApp(true);
    setWhatsAppFeedback(null);

    try {
      const res = await fetch(`/api/bookings/${confirmedBooking.id}/send-whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        setWhatsAppSent(true);
        setWhatsAppFeedback(`✓ Confirmación enviada con éxito a tu WhatsApp`);
      } else {
        setWhatsAppFeedback(data.error || 'No se pudo enviar el mensaje por WhatsApp');
      }
    } catch {
      setWhatsAppFeedback('Error al comunicarse con WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto px-4 py-16 text-center animate-in zoom-in-95 duration-200">
      {/* Círculo con Tilde Verde idéntico a Imagen 5 */}
      <div className="w-20 h-20 rounded-full border-2 border-[#10b981] flex items-center justify-center mx-auto mb-6 text-[#10b981]">
        <CheckCircle2 className="w-10 h-10 stroke-[2.5]" />
      </div>

      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
        ¡Cita agendada!
      </h1>
      <p className="text-sm sm:text-base text-gray-500 mt-2 font-normal">
        Tu cita fue confirmada con éxito.
      </p>

      {/* Botones Idénticos a Imagen 5 */}
      <div className="w-full max-w-sm mx-auto mt-8 space-y-3">
        <button
          type="button"
          onClick={onOpenMyBookings}
          className="w-full py-3.5 px-6 rounded-xl bg-[#00966b] hover:bg-[#00825c] text-white font-medium text-base transition shadow-xs cursor-pointer text-center"
        >
          Ver detalles de la cita
        </button>

        <button
          type="button"
          onClick={handleSendWhatsAppConfirmation}
          disabled={sendingWhatsApp}
          className="w-full py-3.5 px-6 rounded-xl border border-[#00966b] bg-white text-[#00966b] hover:bg-emerald-50/50 font-medium text-base transition shadow-2xs cursor-pointer text-center disabled:opacity-60"
        >
          {sendingWhatsApp ? (
            <span>Enviando confirmación...</span>
          ) : whatsAppSent ? (
            <span>¡Confirmación enviada por WhatsApp!</span>
          ) : (
            <span>Enviar confirmación por WhatsApp</span>
          )}
        </button>

        {whatsAppFeedback && (
          <p className={`text-xs text-center font-medium mt-2 animate-in fade-in ${
            whatsAppSent ? 'text-emerald-700' : 'text-amber-700'
          }`}>
            {whatsAppFeedback}
          </p>
        )}
      </div>
    </div>
  );
};
